import { Stack, Grid, Typography, IconButton } from '@mui/material';
import { IProducts } from '../models/interfaces';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Tab, TabOption } from '../shared/tab';
import { format } from 'date-fns/format';
import ProductClusterDetail from './ProductClusterDetail';
import { Link, WarningAmber } from '@mui/icons-material';
import { theme } from '../theme';
import { CustomTooltip } from '../shared/Tooltip/customTooltip';
import { useNavigate } from 'react-router-dom';

interface Props {
  product: IProducts;
}

const ProductDetail = ({ product }: Props) => {
  const { t } = useTranslation();

  const [tabValue, setTabValue] = useState<string>('cluster');

  const navigate = useNavigate();

  const opts: TabOption[] = [
    { label: t('product.cluster'), value: 'cluster' },
    { label: t('product.detail'), value: 'detail' },
    // { label: t('product.clusterManualMatch'), value: 'mmatch' },
  ];

  const fields: { label: string; value: React.ReactNode }[] = [
    { label: t('product.managementId'), value: product.id_gestionale || '-' },
    { label: t('product.structureId'), value: product.id_struttura || '-' },
    { label: t('product.productId'), value: product.id_prodotto || '-' },
    { label: t('product.supplierId'), value: product.id_fornitore || '-' },
    { label: t('product.supplierName'), value: product.nome_fornitore || '-' },
    { label: t('product.vatNumber'), value: product.p_iva || '-' },
    { label: t('product.productName'), value: product.nome_prodotto || '-' },
    { label: t('product.manufacturerRef'), value: product.ref_produttore || '-' },
    { label: t('product.supplierCode'), value: product.codice_fornitore || '-' },
    { label: t('product.rdm'), value: product.rdm || '-' },
    { label: t('product.cnd'), value: product.cnd || '-' },
    { label: t('product.expirationDate'), value: product.fine_validita || '-' },
    { label: t('product.unitPrice'), value: `${product.prezzo_unitario} €` || '-' },
    { label: t('product.upc'), value: product.upc || '-' },
    { label: t('product.clusterId'), value: product.cluster_id || '-' },
    {
      label: t('product.manuallyMatched'),
      value: product.manual_matched
        ? `${t('info.yes')}, ${t('info.at')} ${format(
            new Date(product.manual_matched_at),
            'dd/MM/yyyy hh:mm',
          )} ${t('info.by')}  ${product.manual_matched_by} `
        : t('info.no'),
    },
  ];

  const renderData = () => {
    if (tabValue === 'detail') {
      return (
        <Stack spacing={3} sx={{ height: '100%' }}>
          <Grid container spacing={2}>
            {fields.map(({ label, value }, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontSize={'16px'} color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body1" fontSize={'16px'}>
                    {value}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Stack>
      );
    } else if (tabValue === 'cluster') {
      return (
        <Stack spacing={3}>
          <Typography variant="h4">{t('product.clusterProductAssociated')}</Typography>
          <Typography variant="subtitle2">
            {t('product.clusterId')}
            {' : '}
            {product.cluster_id}
          </Typography>
          <Typography variant="subtitle2">
            {t('product.productName')}
            {' : '}
            {product.nome_prodotto}
          </Typography>
          {product.to_review && (
            <Stack
              spacing={1}
              direction={'row'}
              alignItems={'center'}
              color={theme.palette.warning.main}
              fontSize={'16px'}
            >
              <WarningAmber color="inherit" />
              <Typography variant="body2" color="inherit" fontSize={'inherit'}>
                {t('product.clusterAssociedWarning')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => {
                  navigate('/manualMatch', {
                    state: { stateFilters: { cluster_id: product.cluster_id } },
                  });
                }}
              >
                {t('product.goToManualMatch')}
              </Typography>
            </Stack>
          )}

          <ProductClusterDetail clusterId={product.cluster_id} />
        </Stack>
      );
    }
  };

  return (
    <Stack spacing={1} sx={{ padding: '5px' }}>
      <Tab options={opts} value={tabValue} onChange={(val) => setTabValue(val)}>
        {renderData()}
      </Tab>
    </Stack>
  );
};

export default ProductDetail;
